import { getColumnLetter } from "../workbook/columns";
import { safeWorksheetName } from "../workbook";

export interface FlattenTableFormulaStats {
  originalSheetName: string;
  newSheetName: string;
  usedRangeCellCount: number;
  cellsScanned: number;
  cellsFlattened: number;
}

export const FLATTEN_TABLE_FORMULAS_LABEL = "Flatten Table Formulas";

export const FLATTEN_TABLE_FORMULAS_TOOLTIP =
  "This will create a copy of the current worksheet, but flatten all of the formulas linked to the ediphi data tables. This is often used before sending out an owner bid form";

const shouldFlattenFormula = (formula: unknown): boolean => {
  if (typeof formula !== "string") return false;
  return formula.includes("[");
};

const toA1RowAddress = (rowNumber1Based: number, startCol0Based: number, endCol0Based: number): string => {
  const start = `${getColumnLetter(startCol0Based)}${rowNumber1Based}`;
  if (startCol0Based === endCol0Based) return start;
  const end = `${getColumnLetter(endCol0Based)}${rowNumber1Based}`;
  return `${start}:${end}`;
};

/**
 * Creates a copy of the active worksheet (adjacent to the original) and replaces
 * formulas with values **only** when the formula contains `[`.
 */
export const flattenTableReferencingFormulasOnActiveSheet = async (): Promise<FlattenTableFormulaStats> => {
  return await Excel.run(async (context) => {
    const application = context.workbook.application;

    try {
      // Disable automatic calculation for performance while we copy + bulk-rewrite formulas.
      application.calculationMode = Excel.CalculationMode.manual;
      await context.sync();

      const originalSheet = context.workbook.worksheets.getActiveWorksheet();
      originalSheet.load("name");
      await context.sync();

      // Copy next to original
      const newSheet = originalSheet.copy(Excel.WorksheetPositionType.after, originalSheet);
      await context.sync();

      // New sheet naming: "FLAT " + original (trim right to keep <= 31)
      const prefix = "FLAT ";
      const maxNameLen = 31;
      const trimmedOriginal = originalSheet.name.slice(0, Math.max(0, maxNameLen - prefix.length));
      const baseName = `${prefix}${trimmedOriginal}`;
      const uniqueName = await safeWorksheetName(context.workbook, baseName, true);
      newSheet.name = uniqueName;

      // Scan used range
      const usedRange = newSheet.getUsedRangeOrNullObject();
      usedRange.load("isNullObject,rowIndex,columnIndex,rowCount,columnCount");
      await context.sync();

      if (usedRange.isNullObject) {
        newSheet.activate();
        await context.sync();
        return {
          originalSheetName: originalSheet.name,
          newSheetName: uniqueName,
          usedRangeCellCount: 0,
          cellsScanned: 0,
          cellsFlattened: 0,
        };
      }

      const rowCount = usedRange.rowCount;
      const colCount = usedRange.columnCount;
      const usedRangeCellCount = rowCount * colCount;

      let cellsFlattened = 0;

      // For large used ranges, loading `Range.formulas` for the entire rectangle can exceed Office.js payload limits
      // (notably ~5 MB request/response on Excel for the web), causing partial/empty formula data.
      //
      // MSFT docs: Excel on the web limits requests/responses to 5 MB:
      // https://learn.microsoft.com/en-us/office/dev/add-ins/excel/performance#payload-size-limit-best-practices
      //
      // Best practice is to process in tiles sized by *cells per request* so each `load("formulas")` stays
      // small/reliable.

      const cellBudgetPerFormulaLoad = 50_000;
      const writeChunkSize = 10_000;

      // Prefer row-batching only (simplest): load full width if possible.
      let batchColSize = colCount;
      let batchRowSize = Math.floor(cellBudgetPerFormulaLoad / Math.max(1, batchColSize));

      // If the sheet is extremely wide, a single row can exceed the budget. In that case, fall back to
      // column-tiling as well, still derived from the same budget.
      if (batchRowSize < 1) {
        batchRowSize = 1;
        batchColSize = Math.max(1, Math.min(colCount, cellBudgetPerFormulaLoad));
      }

      for (let r0 = 0; r0 < rowCount; r0 += batchRowSize) {
        const batchRows = Math.min(batchRowSize, rowCount - r0);

        for (let c0 = 0; c0 < colCount; c0 += batchColSize) {
          const batchCols = Math.min(batchColSize, colCount - c0);
          const batchRange = newSheet.getRangeByIndexes(
            usedRange.rowIndex + r0,
            usedRange.columnIndex + c0,
            batchRows,
            batchCols
          );
          batchRange.load("formulas");
          await context.sync();

          const batchFormulas = (batchRange.formulas as unknown[][]) || [];

          const segments: Array<{ address: string; cellCount: number }> = [];
          for (let r = 0; r < batchRows; r++) {
            const rowFormulas = batchFormulas[r] || [];

            let runStart = -1;
            for (let c = 0; c <= batchCols; c++) {
              const isTarget = c < batchCols ? shouldFlattenFormula(rowFormulas[c]) : false; // sentinel at end
              if (isTarget && runStart === -1) {
                runStart = c;
              } else if (!isTarget && runStart !== -1) {
                const runEnd = c - 1;
                const rowNumber1Based = usedRange.rowIndex + r0 + r + 1;
                const startCol0Based = usedRange.columnIndex + c0 + runStart;
                const endCol0Based = usedRange.columnIndex + c0 + runEnd;
                const address = toA1RowAddress(rowNumber1Based, startCol0Based, endCol0Based);

                const cellCount = runEnd - runStart + 1;
                segments.push({ address, cellCount });
                cellsFlattened += cellCount;

                runStart = -1;
              }
            }
          }

          for (let i = 0; i < segments.length; i += writeChunkSize) {
            const chunk = segments.slice(i, i + writeChunkSize);
            for (const seg of chunk) {
              const range = newSheet.getRange(seg.address);
              range.copyFrom(range, Excel.RangeCopyType.values);
            }
            await context.sync();
          }
        }
      }

      newSheet.activate();
      await context.sync();

      return {
        originalSheetName: originalSheet.name,
        newSheetName: uniqueName,
        usedRangeCellCount,
        cellsScanned: usedRangeCellCount,
        cellsFlattened,
      };
    } finally {
      // Best-effort: ensure calculation is returned to Automatic even if the flatten fails.
      try {
        application.calculationMode = Excel.CalculationMode.automatic;
        await context.sync();
      } catch (restoreError) {
        console.warn("Failed to restore Excel calculation mode to AUTOMATIC", restoreError);
      }
    }
  });
};
